import React from 'react';
import { COMPRA_SIMPLE_TEMPLATE, CON_RENTA_TEMPLATE, PRO_TEMPLATE } from '../lib/quoteTemplates';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: sincroniza el valor interno del editor de código con el prop `value` (editor controlado).
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const RT_COLORS = [
  { label: 'Blanco',   value: '#ffffff' },
  { label: 'Negro',    value: '#111111' },
  { label: 'Rojo',     value: '#ef4444' },
  { label: 'Naranja',  value: '#f97316' },
  { label: 'Amarillo', value: '#eab308' },
  { label: 'Verde',    value: '#10b981' },
  { label: 'Azul',     value: '#3b82f6' },
  { label: 'Morado',   value: '#a855f7' },
  { label: 'Rosa',     value: '#ec4899' },
];

const highlightHTML = (code) => {
  if (!code) return '';
  let escaped = code
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Color tag names
  escaped = escaped.replace(/(&lt;\/?[a-zA-Z0-9-]+)/g, '<span style="color:#f472b6;font-weight:600;">$1</span>');
  // Color tag closing symbol
  escaped = escaped.replace(/(&gt;)/g, '<span style="color:#f472b6;font-weight:600;">$1</span>');
  // Color attributes
  escaped = escaped.replace(/(\s[a-zA-Z0-9-]+)(=)/g, '<span style="color:#fbbf24;">$1</span><span style="color:#e2e8f0;">$2</span>');
  // Color string attribute values
  escaped = escaped.replace(/(["'][^"']*["'])/g, '<span style="color:#34d399;">$1</span>');
  // Color HTML comments
  escaped = escaped.replace(/(&lt;!--.*?--&gt;)/g, '<span style="color:#9ca3af;font-style:italic;">$1</span>');

  return escaped;
};

const TABLE_GRID = 8;

const BtnStyle = { background:'transparent', border:'none', color:'rgba(255,255,255,0.7)', cursor:'pointer',
  padding:'var(--space-1) var(--space-2)', borderRadius:'var(--radius-sm)', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center',
  transition:'background 0.15s', lineHeight:1 };

// Extraído a scope de módulo (02.3-client.md §1/§5/§7 riesgo 4, paso b) -- vivía como
// `const ColorPalette = (...) => ...` dentro de `RichTextEditor`, una violación de
// `react-hooks/static-components` (aunque sin closures reales: solo usa `RT_COLORS`, ya
// module-level, así que el hoist es mecánico, sin contrato de props que rediseñar).
function ColorPalette({ onPick, onRainbow, onClose }) {
  return (
    <div onMouseDown={e => e.stopPropagation()}
      style={{ position:'absolute', zIndex:9999, top:'100%', left:0, marginTop:'var(--space-1)',
        background:'#1c1c1f', border:'1px solid #333', borderRadius:'var(--radius-md)', padding:'var(--space-2)',
        display:'grid', gridTemplateColumns:'repeat(5,22px)', gap:'var(--space-1)', boxShadow:'0 8px 24px rgba(0,0,0,0.6)' }}>
      {RT_COLORS.map(c => (
        <div key={c.value} title={c.label} onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onPick(c.value); onClose(); }}
          style={{ width:22, height:22, borderRadius:'var(--radius-sm)', background:c.value, border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer' }} />
      ))}
      <div title="Arcoiris" onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onRainbow(); onClose(); }}
        style={{ gridColumn:'span 5', height:22, borderRadius:'var(--radius-sm)', cursor:'pointer',
          background:'linear-gradient(90deg,#ef4444,#f97316,#eab308,#10b981,#3b82f6,#a855f7,#ec4899)',
          border:'1px solid rgba(255,255,255,0.1)' }} />
    </div>
  );
}

// Extraído a scope de módulo (02.3-client.md §1/§5/§7 riesgo 4, paso b) -- fix obligatorio, no
// una pregunta abierta de "engine core": era un componente anidado real dentro de
// `RichTextEditor` (violación de `react-hooks/static-components`), independiente del veredicto
// que tome el paso `c` sobre el resto del archivo. Cerraba sobre ~15-20 valores del padre --
// ahora contrato de props explícito, sin bolsa de `{...everything}`. Comportamiento visible
// idéntico, estilos inline verbatim (conversión a tokens es el paso `e`).
function Toolbar({
  isFloat, isCodeMode, selectedFontSize, colorState, setColorState,
  showTablePicker, setShowTablePicker, hoverCell, setHoverCell, tablePickerRef,
  onFontSizeDelta, onFormatBlock, onExec, onSaveSelection, onRestoreSelection, onRainbow,
  onInsertTable, onToggleCodeMode, onSelectTemplate,
}) {
  return (
    <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:2,
      background: isFloat ? '#1c1c1f' : '#161618',
      borderRadius: isFloat ? 8 : 0,
      border: isFloat ? '1px solid #333' : 'none',
      padding: isFloat ? '4px 6px' : '6px 10px',
      boxShadow: isFloat ? '0 4px 20px rgba(0,0,0,0.7)' : 'none',
      borderBottom: isFloat ? 'none' : '1px solid #2a2a2e',
      width: '100%', justifyContent: 'flex-start' }}>

      {/* Font size +/- controls with current px indicator */}
      <div style={{ display:'flex', alignItems:'center', gap: '2px', background: 'rgba(255,255,255,0.03)', borderRadius: 'var(--radius-sm)', padding: '1px var(--space-1)' }}>
        <button style={{...BtnStyle, fontWeight:700, padding:'2px var(--space-1)'}} title="Reducir letra (-2px)" aria-label="Reducir letra"
          onMouseDown={e=>{e.preventDefault(); onFontSizeDelta(-2);}}>
          A−
        </button>
        <span style={{ fontSize: 10, color: '#f97316', padding: '0 var(--space-1)', fontFamily: 'monospace', fontWeight: 600 }}>
          {selectedFontSize}
        </span>
        <button style={{...BtnStyle, fontWeight:700, padding:'2px var(--space-1)'}} title="Aumentar letra (+2px)" aria-label="Aumentar letra"
          onMouseDown={e=>{e.preventDefault(); onFontSizeDelta(2);}}>
          A+
        </button>
      </div>

      <div style={{width:1, height:16, background:'#333', margin:'0 var(--space-1)'}}/>

      {/* Heading type */}
      {!isCodeMode && (
        <>
          <select onMouseDown={e=>e.stopPropagation()}
            aria-label="Tipo de encabezado"
            onChange={e=>{onFormatBlock(e.target.value); e.target.value='P';}}
            defaultValue="P"
            style={{ background:'#0d0d0f', color:'rgba(255,255,255,0.75)', border:'1px solid #333',
              borderRadius:'var(--radius-sm)', padding:'2px var(--space-1)', fontSize:11, cursor:'pointer', outline:'none' }}>
            <option value="P">Normal</option>
            <option value="H1">H1</option>
            <option value="H2">H2</option>
            <option value="H3">H3</option>
            <option value="H4">H4</option>
          </select>
          <div style={{width:1, height:16, background:'#333', margin:'0 var(--space-1)'}}/>
        </>
      )}

      {/* Bold / Italic / Underline */}
      {!isCodeMode && (
        <>
          <button style={{...BtnStyle, fontWeight:700}} title="Negrita" aria-label="Negrita" onMouseDown={e=>{e.preventDefault(); onExec('bold');}}>B</button>
          <button style={{...BtnStyle, fontStyle:'italic'}} title="Cursiva" aria-label="Cursiva" onMouseDown={e=>{e.preventDefault(); onExec('italic');}}>I</button>
          <button style={{...BtnStyle, textDecoration:'underline'}} title="Subrayado" aria-label="Subrayado" onMouseDown={e=>{e.preventDefault(); onExec('underline');}}>U</button>
          <div style={{width:1, height:16, background:'#333', margin:'0 var(--space-1)'}}/>
        </>
      )}

      {/* Text color */}
      {!isCodeMode && (
        <div style={{position:'relative'}} onMouseDown={e=>e.stopPropagation()}>
          <button style={{...BtnStyle, gap:'var(--space-1)'}} title="Color de texto" aria-label="Color de texto"
            onMouseDown={e=>{e.preventDefault(); onSaveSelection(); setColorState(p => p==='fore' ? null : 'fore'); }}>
            <span style={{fontWeight:700, fontSize:13}}>A</span>
            <span style={{width:16, height:3, borderRadius:1, background:'linear-gradient(90deg,#ef4444,#3b82f6,#10b981)', display:'block'}}/>
          </button>
          {colorState === 'fore' && (
            <ColorPalette
              onPick={v => { onRestoreSelection(); onExec('foreColor', v); }}
              onRainbow={() => { onRestoreSelection(); onRainbow(); }}
              onClose={() => setColorState(null)}/>
          )}
        </div>
      )}

      {/* Highlight (background color) */}
      {!isCodeMode && (
        <div style={{position:'relative'}} onMouseDown={e=>e.stopPropagation()}>
          <button style={{...BtnStyle, gap:'var(--space-1)'}} title="Resaltar texto" aria-label="Resaltar texto"
            onMouseDown={e=>{e.preventDefault(); onSaveSelection(); setColorState(p => p==='back' ? null : 'back'); }}>
            <span style={{fontSize:13}}>🖊</span>
            <span style={{width:16, height:3, borderRadius:1, background:'linear-gradient(90deg,#eab308,#ec4899,#a855f7)', display:'block'}}/>
          </button>
          {colorState === 'back' && (
            <ColorPalette
              onPick={v => { onRestoreSelection(); onExec('hiliteColor', v); }}
              onRainbow={() => { onRestoreSelection(); onRainbow(); }}
              onClose={() => setColorState(null)}/>
          )}
        </div>
      )}

      {!isCodeMode && <div style={{width:1, height:16, background:'#333', margin:'0 var(--space-1)'}}/>}

      {/* Table picker — only in fixed toolbar */}
      {!isFloat && !isCodeMode && (
        <div style={{position:'relative'}} onMouseDown={e=>e.stopPropagation()} ref={tablePickerRef}>
          <button style={{...BtnStyle, fontSize:11}} title="Insertar tabla"
            onMouseDown={e=>{e.preventDefault(); onSaveSelection(); setShowTablePicker(p=>!p); }}>
            ⊞ Tabla
          </button>
          {showTablePicker && (
            <div onMouseDown={e=>e.stopPropagation()}
              style={{ position:'absolute', top:'100%', left:0, marginTop:'var(--space-1)', zIndex:9999,
                background:'#1c1c1f', border:'1px solid #333', borderRadius:'var(--radius-md)', padding:'var(--space-3)',
                boxShadow:'0 8px 24px rgba(0,0,0,0.6)' }}>
              <div style={{fontSize:10, color:'rgba(255,255,255,0.4)', marginBottom:'var(--space-2)'}}>
                {hoverCell.r > 0 ? `${hoverCell.r} × ${hoverCell.c}` : 'Selecciona filas × columnas'}
              </div>
              <div style={{display:'grid', gridTemplateColumns:`repeat(${TABLE_GRID},18px)`, gap:2}}>
                {Array.from({length: TABLE_GRID * TABLE_GRID}).map((_, i) => {
                  const r = Math.floor(i / TABLE_GRID) + 1;
                  const c = (i % TABLE_GRID) + 1;
                  const active = r <= hoverCell.r && c <= hoverCell.c;
                  return (
                    <div key={i}
                      onMouseEnter={() => setHoverCell({r, c})}
                      onMouseDown={e => { e.preventDefault(); onInsertTable(r, c); }}
                      style={{ width:18, height:18, borderRadius:2, cursor:'pointer',
                        background: active ? 'rgba(249,115,22,0.4)' : 'rgba(255,255,255,0.07)',
                        border: active ? '1px solid rgba(249,115,22,0.6)' : '1px solid rgba(255,255,255,0.1)',
                        transition:'background 0.1s' }} />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!isFloat && !isCodeMode && <div style={{width:1, height:16, background:'#333', margin:'0 var(--space-1)'}}/>}

      {/* Code Mode Toggle Button */}
      {!isFloat && (
        <button style={{...BtnStyle, color: isCodeMode ? '#f97316' : '#3b82f6', fontWeight: 600, gap: 'var(--space-1)'}}
          title={isCodeMode ? 'Ver Editor Visual' : 'Ver Código HTML'}
          onMouseDown={e=>{e.preventDefault(); onToggleCodeMode();}}>
          {isCodeMode ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Vista
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
              Código
            </>
          )}
        </button>
      )}

      {/* Templates Dropdown — placed right after code button */}
      {!isFloat && (
        <>
          <div style={{width:1, height:16, background:'#333', margin:'0 var(--space-1)'}}/>
          <select
            onMouseDown={e=>e.stopPropagation()}
            aria-label="Insertar plantilla"
            onChange={e => {
              onSelectTemplate(e.target.value);
              e.target.value = '';
            }}
            defaultValue=""
            style={{ background:'#0d0d0f', color:'#f97316', border:'1px solid rgba(249,115,22,0.3)',
              borderRadius:'var(--radius-sm)', padding:'2px var(--space-2)', fontSize:11, fontWeight:600, cursor:'pointer', outline:'none' }}>
            <option value="" disabled>📝 Templates</option>
            <option value="simple">Compra Simple</option>
            <option value="renta">Con Renta</option>
            <option value="pro">Plan Pro</option>
          </select>
        </>
      )}
    </div>
  );
}

export default function RichTextEditor({ value, onChange, disabled, minHeight = 220 }) {
  const editorRef = React.useRef(null);
  const floatRef = React.useRef(null);
  const tablePickerRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const preRef = React.useRef(null);

  const [isCodeMode, setIsCodeMode] = React.useState(false);
  const [codeValue, setCodeValue] = React.useState(value || '');
  const [floatVisible, setFloatVisible] = React.useState(false);
  const [floatPos, setFloatPos] = React.useState({ top: 0, left: 0 });
  const [showTablePicker, setShowTablePicker] = React.useState(false);
  const [hoverCell, setHoverCell] = React.useState({ r: 0, c: 0 });
  const [showColorPick, setShowColorPick] = React.useState(null);
  const [showFloatColor, setShowFloatColor] = React.useState(null);
  const [selectedFontSize, setSelectedFontSize] = React.useState('13px');
  const savedRange = React.useRef(null);

  // Sync value → editor state
  React.useEffect(() => {
    if (isCodeMode) {
      setCodeValue(value || '');
    } else {
      const el = editorRef.current;
      if (el && el.innerHTML !== (value || '')) {
        el.innerHTML = value || '';
      }
    }
  }, [value, isCodeMode]);

  const emit = (val) => {
    onChange(val);
  };

  const exec = (cmd, val = null) => {
    if (isCodeMode) return;
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    emit(editorRef.current.innerHTML);
  };

  const execFontSize = (delta) => {
    if (isCodeMode) return;

    // Custom selection handling for custom increment
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);

    let parent = sel.anchorNode;
    if (parent.nodeType === 3) {
      parent = parent.parentNode;
    }
    const computedStyle = window.getComputedStyle(parent);
    let currentSize = parseInt(computedStyle.fontSize) || 13;
    let newSize = Math.max(8, Math.min(72, currentSize + delta));

    // Wrap selection in span with custom size
    const span = document.createElement('span');
    span.style.fontSize = `${newSize}px`;
    span.appendChild(range.extractContents());
    range.insertNode(span);

    setSelectedFontSize(`${newSize}px`);
    emit(editorRef.current.innerHTML);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRange.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const applyRainbow = () => {
    if (isCodeMode) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement('span');
    span.style.backgroundImage = 'linear-gradient(90deg,#ef4444,#f97316,#eab308,#10b981,#3b82f6,#a855f7,#ec4899)';
    span.style.webkitBackgroundClip = 'text';
    span.style.backgroundClip = 'text';
    span.style.color = 'transparent';
    span.style.display = 'inline';
    span.appendChild(range.extractContents());
    range.insertNode(span);
    emit(editorRef.current.innerHTML);
    setFloatVisible(false);
  };

  const insertTable = (rows, cols) => {
    if (isCodeMode) return;
    editorRef.current?.focus();
    if (savedRange.current) restoreSelection();
    let html = `<table style="width:100%;border-collapse:collapse;margin:12px 0">`;
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        html += `<td style="border:1px solid rgba(255,255,255,0.2);padding:8px 12px;min-width:60px;background:rgba(255,255,255,0.03)" contenteditable="true">&nbsp;</td>`;
      }
      html += '</tr>';
    }
    html += '</table><p><br></p>';
    document.execCommand('insertHTML', false, html);
    emit(editorRef.current.innerHTML);
    setShowTablePicker(false);
    setFloatVisible(false);
  };

  const onSelectionChange = () => {
    if (isCodeMode) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !editorRef.current?.contains(sel.anchorNode)) {
      setFloatVisible(false);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect  = range.getBoundingClientRect();
    const editorRect = editorRef.current.getBoundingClientRect();

    let parent = sel.anchorNode;
    if (parent.nodeType === 3) parent = parent.parentNode;
    const computedStyle = window.getComputedStyle(parent);
    setSelectedFontSize(computedStyle.fontSize || '13px');

    setFloatPos({
      top:  rect.top  - editorRect.top  - 50,
      left: Math.min(rect.left - editorRect.left, editorRect.width - 280),
    });
    setFloatVisible(true);
  };

  React.useEffect(() => {
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [isCodeMode]);

  React.useEffect(() => {
    const close = (e) => {
      setShowColorPick(null);
      setShowFloatColor(null);
      if (!tablePickerRef.current?.contains(e.target)) setShowTablePicker(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleTemplateSelect = (type) => {
    let content = '';
    if (type === 'simple') content = COMPRA_SIMPLE_TEMPLATE;
    else if (type === 'renta') content = CON_RENTA_TEMPLATE;
    else if (type === 'pro') content = PRO_TEMPLATE;

    if (content) {
      if (isCodeMode) {
        setCodeValue(content);
      } else if (editorRef.current) {
        editorRef.current.innerHTML = content;
      }
      emit(content);
    }
  };

  const toggleCodeMode = () => {
    if (isCodeMode) {
      // Code → WYSIWYG
      setIsCodeMode(false);
      emit(codeValue);
    } else {
      // WYSIWYG → Code
      const currentHtml = editorRef.current ? editorRef.current.innerHTML : value;
      setCodeValue(currentHtml || '');
      setIsCodeMode(true);
    }
  };

  const handleFormatBlock = (val) => { editorRef.current?.focus(); exec('formatBlock', val); };

  const handleTextareaScroll = (e) => {
    if (preRef.current) {
      preRef.current.scrollTop = e.target.scrollTop;
      preRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  return (
    <div style={{position:'relative', display:'flex', flexDirection:'column',
      background:'#0a0a0c', border:'1px solid #2a2a2e', borderRadius:'var(--radius-md)', overflow:'visible'}}>

      {/* Fixed toolbar */}
      {!disabled && (
        <Toolbar
          isFloat={false}
          isCodeMode={isCodeMode}
          selectedFontSize={selectedFontSize}
          colorState={showColorPick}
          setColorState={setShowColorPick}
          showTablePicker={showTablePicker}
          setShowTablePicker={setShowTablePicker}
          hoverCell={hoverCell}
          setHoverCell={setHoverCell}
          tablePickerRef={tablePickerRef}
          onFontSizeDelta={execFontSize}
          onFormatBlock={handleFormatBlock}
          onExec={exec}
          onSaveSelection={saveSelection}
          onRestoreSelection={restoreSelection}
          onRainbow={applyRainbow}
          onInsertTable={insertTable}
          onToggleCodeMode={toggleCodeMode}
          onSelectTemplate={handleTemplateSelect}
        />
      )}

      {/* Floating selection toolbar */}
      {!disabled && floatVisible && !isCodeMode && (
        <div ref={floatRef}
          style={{ position:'absolute', top: floatPos.top, left: Math.max(0, floatPos.left),
            zIndex:9999, pointerEvents:'all' }}>
          <Toolbar
            isFloat={true}
            isCodeMode={isCodeMode}
            selectedFontSize={selectedFontSize}
            colorState={showFloatColor}
            setColorState={setShowFloatColor}
            showTablePicker={showTablePicker}
            setShowTablePicker={setShowTablePicker}
            hoverCell={hoverCell}
            setHoverCell={setHoverCell}
            tablePickerRef={tablePickerRef}
            onFontSizeDelta={execFontSize}
            onFormatBlock={handleFormatBlock}
            onExec={exec}
            onSaveSelection={saveSelection}
            onRestoreSelection={restoreSelection}
            onRainbow={applyRainbow}
            onInsertTable={insertTable}
            onToggleCodeMode={toggleCodeMode}
            onSelectTemplate={handleTemplateSelect}
          />
        </div>
      )}

      {/* Editable Area / Code Area.
          `key` distinto por rama a propósito: la rama WYSIWYG llena su `<div>` vía
          `editorRef.current.innerHTML =` (fuera del vdom de React), así que al cambiar de rama
          React reusaría el mismo `<div>` y no limpiaría esos hijos imperativos -- el HTML
          renderizado quedaba superpuesto sobre la vista de código. Con keys distintas React
          desmonta el nodo viejo entero y monta uno limpio (02.3-client.md §7 riesgo 12). */}
      {isCodeMode ? (
        <div key="rte-code" style={{ position: 'relative', minHeight, width: '100%', background: '#070709', borderRadius: '0 0 var(--radius-md) var(--radius-md)', overflow: 'hidden' }}>
          {/* pre-rendered syntax highlighted layer */}
          <pre
            ref={preRef}
            dangerouslySetInnerHTML={{ __html: highlightHTML(codeValue) }}
            style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              margin: 0, padding: 'var(--space-4) var(--space-4)',
              fontFamily: "'Fira Code', 'Courier New', Courier, monospace",
              fontSize: 13, lineHeight: 1.7,
              color: 'rgba(255,255,255,0.85)',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              overflow: 'hidden', pointerEvents: 'none',
              background: 'transparent'
            }}
          />
          {/* transparent interactive textarea layer */}
          <textarea
            ref={textareaRef}
            aria-label="Editor de código HTML"
            value={codeValue}
            onChange={e => {
              setCodeValue(e.target.value);
              emit(e.target.value);
            }}
            onScroll={handleTextareaScroll}
            style={{
              width: '100%', minHeight,
              background: 'transparent', color: 'transparent', caretColor: '#f97316',
              border: 'none', outline: 'none',
              padding: 'var(--space-4) var(--space-4)',
              fontFamily: "'Fira Code', 'Courier New', Courier, monospace",
              fontSize: 13, lineHeight: 1.7,
              resize: 'vertical',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all'
            }}
          />
        </div>
      ) : (
        <div
          key="rte-wysiwyg"
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={() => emit(editorRef.current.innerHTML)}
          onMouseUp={onSelectionChange}
          onKeyUp={onSelectionChange}
          style={{ minHeight, padding:'var(--space-4) var(--space-4)', color:'rgba(255,255,255,0.9)', outline:'none',
            fontSize:13, lineHeight:1.7, overflowY:'auto',
            fontFamily:"'Inter', -apple-system, sans-serif" }}
        />
      )}
    </div>
  );
}
